<template>
  <div class="h-full flex flex-col p-4">
    <base-card class="h-full flex flex-col">
      <div class="px-6 py-4 border-b">
        <h1 class="text-xl font-semibold">Chrome Extension</h1>
        <p class="text-sm text-gray-500 mt-1">
          Generate the credential for your branch's Chrome Extension and control whether your banks accept extension auto-approvals.
        </p>
      </div>

      <base-spinner v-if="isLoading" class="flex-auto"></base-spinner>

      <div v-else class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- Bank coverage toggle -->
        <div class="border rounded-lg p-5">
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <h2 class="text-base font-semibold">Auto-approve enabled on banks</h2>
              <p class="text-xs text-gray-500 mt-1">
                When ON, every bank under this branch will accept Chrome Extension auto-approvals. Toggle OFF to disable across all of them.
              </p>
              <p class="text-sm mt-2">
                <span class="font-medium">{{ enabledBanksCount }}</span>
                of
                <span class="font-medium">{{ totalBanksCount }}</span>
                banks currently enabled
              </p>
            </div>
            <div class="flex flex-col items-end gap-2">
              <label class="switch">
                <input type="checkbox" :checked="allEnabled" :disabled="totalBanksCount === 0 || isToggling" @change="onToggleEnabled($event.target.checked)" />
                <span class="slider"></span>
              </label>
              <span v-if="totalBanksCount === 0" class="text-xs text-gray-500">No banks under this branch yet.</span>
            </div>
          </div>
        </div>

        <!-- Credential -->
        <div class="border rounded-lg p-5">
          <div class="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 class="text-base font-semibold">Credential token</h2>
              <p class="text-xs text-gray-500 mt-1">
                Paste this into the Chrome Extension popup. Treat it like a password — anyone with this token can approve orders for your branch's banks.
              </p>
            </div>
            <span :class="badgeClass" class="px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
              {{ badgeText }}
            </span>
          </div>

          <!-- Active, non-expired credential -->
          <div v-if="hasCredential && !expired" class="space-y-3">
            <div class="bg-gray-50 border rounded-md px-3 py-2 flex items-center gap-2">
              <input
                ref="tokenInput"
                :type="reveal ? 'text' : 'password'"
                readonly
                :value="credential"
                class="flex-1 bg-transparent text-xs font-mono outline-none border-0"
              />
              <button class="text-xs px-2 py-1 rounded border hover:bg-gray-100" @click="reveal = !reveal">
                {{ reveal ? 'Hide' : 'Show' }}
              </button>
              <button class="text-xs px-2 py-1 rounded border hover:bg-gray-100" @click="copyToken">
                {{ copied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
            <div class="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>Fingerprint: <span class="font-mono">{{ fingerprint }}</span></span>
              <span v-if="createdAt">Generated: <span class="text-gray-700">{{ formatDate(createdAt) }}</span></span>
              <span v-if="expiresAt">Expires: <span :class="expiryColorClass" class="font-medium">{{ expiryHuman }}</span> ({{ formatDate(expiresAt) }})</span>
            </div>
            <div class="flex gap-2">
              <button class="px-3 py-1.5 rounded text-xs font-semibold border border-amber-500 text-amber-600 hover:bg-amber-50" @click="onRotate" :disabled="isGenerating">
                {{ isGenerating ? 'Generating…' : 'Rotate credential' }}
              </button>
            </div>
            <p class="text-xs text-amber-700">Credentials expire <strong>6 hours</strong> after generation. Rotate immediately invalidates the existing token — any extension still on the old one will stop working until you paste the new one.</p>
          </div>

          <!-- Expired credential -->
          <div v-else-if="expired" class="space-y-3">
            <div class="bg-amber-50 border border-amber-300 rounded-md p-3 text-amber-900 text-sm">
              <p class="font-semibold mb-1">Credential expired</p>
              <p class="text-xs">
                Your previous credential expired at <strong>{{ formatDate(expiresAt) }}</strong>. Extensions using it are now being rejected. Generate a new credential and re-paste it into every extension instance.
              </p>
              <p class="text-xs mt-1">Pushes captured during the expiry window are buffered locally in each extension and will replay automatically once the new token is pasted.</p>
            </div>
            <!-- Inline style mirrors the bg-red-600/hover:bg-red-700 classes so
                 the button remains visible even if a stale Tailwind build (from
                 before this branch was added) hasn't included those utilities. -->
            <button
              class="px-4 py-2 rounded text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
              style="background-color: #dc2626; color: #ffffff;"
              @mouseover="$event.target.style.backgroundColor='#b91c1c'"
              @mouseleave="$event.target.style.backgroundColor='#dc2626'"
              @click="onGenerate"
              :disabled="isGenerating"
            >
              {{ isGenerating ? 'Generating…' : 'Generate new credential' }}
            </button>
          </div>

          <!-- First-time setup -->
          <div v-else>
            <button class="px-4 py-2 rounded text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700" @click="onGenerate" :disabled="isGenerating">
              {{ isGenerating ? 'Generating…' : 'Generate credential' }}
            </button>
          </div>
        </div>

        <!-- Inline instructions -->
        <div class="border rounded-lg p-5 bg-blue-50 text-sm">
          <h3 class="font-semibold mb-2">How to use this</h3>
          <ol class="list-decimal pl-5 space-y-1 text-gray-700">
            <li>Generate the credential above and copy it.</li>
            <li>Install the Chrome Extension in the bank's browser.</li>
            <li>Open the extension popup, paste the credential token, click Save.</li>
            <li>Toggle Auto-Detect ON in the popup.</li>
            <li>Confirm the bank-coverage toggle above is ON so the banks will accept the extension's calls.</li>
          </ol>
        </div>
      </div>
    </base-card>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "extensions",
  data() {
    return {
      isLoading: true,
      isGenerating: false,
      isToggling: false,
      hasCredential: false,
      credential: "",
      fingerprint: "",
      createdAt: null,
      expiresAt: null,
      expired: false,
      enabledBanksCount: 0,
      totalBanksCount: 0,
      allEnabled: false,
      reveal: false,
      copied: false,
      // Force the expiry-countdown computed properties to re-evaluate every
      // minute so the operator sees "Expires in 3h 12m" tick down without
      // having to reload the page.
      now: Date.now(),
      nowTimer: null,
    };
  },
  computed: {
    badgeClass() {
      if (!this.hasCredential) return 'badge badge-red';
      if (this.expired) return 'badge badge-amber';
      return 'badge badge-green';
    },
    badgeText() {
      if (!this.hasCredential) return 'Not configured';
      if (this.expired) return 'Expired';
      return 'Configured';
    },
    expiryMs() {
      if (!this.expiresAt) return null;
      return new Date(this.expiresAt).getTime() - this.now;
    },
    expiryHuman() {
      const ms = this.expiryMs;
      if (ms == null) return '—';
      if (ms <= 0) return 'expired';
      const totalMin = Math.floor(ms / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      if (h > 0) return `in ${h}h ${m}m`;
      return `in ${m}m`;
    },
    expiryColorClass() {
      const ms = this.expiryMs;
      if (ms == null) return 'text-gray-700';
      if (ms <= 0) return 'text-red-600';
      if (ms < 30 * 60 * 1000) return 'text-amber-600'; // < 30m
      return 'text-emerald-600';
    },
  },
  async created() {
    await this.loadCredential();
    this.nowTimer = setInterval(() => { this.now = Date.now(); }, 60000);
  },
  beforeUnmount() {
    if (this.nowTimer) clearInterval(this.nowTimer);
  },
  methods: {
    async loadCredential() {
      this.isLoading = true;
      try {
        const { data } = await http.get("/branch/extension-credential");
        this.applyData(data?.data || {});
      } catch (err) {
        console.error("loadCredential failed", err);
        this.$swal?.fire?.({ icon: "error", title: "Could not load credential", text: err?.response?.data?.message || err.message });
      } finally {
        this.isLoading = false;
      }
    },
    applyData(d) {
      this.hasCredential = !!d.hasCredential;
      this.credential = d.credential || "";
      this.fingerprint = d.fingerprint || "";
      this.createdAt = d.createdAt || null;
      this.expiresAt = d.expiresAt || null;
      this.expired = !!d.expired;
      this.enabledBanksCount = d.enabledBanksCount ?? 0;
      this.totalBanksCount = d.totalBanksCount ?? 0;
      this.allEnabled = !!d.allEnabled;
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);
      return d.toLocaleString();
    },
    async onGenerate() {
      const confirm = await this.$swal.fire({
        title: this.hasCredential ? "Rotate credential?" : "Generate credential?",
        text: this.hasCredential
          ? "This will invalidate the current token immediately. Existing extensions using it will stop working."
          : "Generate the credential token for your branch.",
        icon: this.hasCredential ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: this.hasCredential ? "Yes, rotate" : "Generate",
        confirmButtonColor: this.hasCredential ? "#d97706" : "#2563eb",
      });
      if (!confirm.isConfirmed) return;

      this.isGenerating = true;
      try {
        const { data } = await http.post("/branch/extension-credential");
        const next = data?.data || {};
        this.credential = next.credential || "";
        this.fingerprint = next.fingerprint || "";
        this.createdAt = next.createdAt || null;
        this.expiresAt = next.expiresAt || null;
        this.expired = false;
        this.hasCredential = !!this.credential;
        this.reveal = true;
        this.$swal.fire({
          icon: "success",
          title: "Credential generated",
          text: "Copy it from the field below and paste it into every extension instance. The token is valid for 6 hours.",
        });
      } catch (err) {
        this.$swal.fire({ icon: "error", title: "Failed to generate", text: err?.response?.data?.message || err.message });
      } finally {
        this.isGenerating = false;
      }
    },
    onRotate() {
      this.onGenerate();
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.credential);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1500);
      } catch (err) {
        // Fallback for older browsers
        if (this.$refs.tokenInput) {
          this.$refs.tokenInput.select();
          document.execCommand("copy");
          this.copied = true;
          setTimeout(() => { this.copied = false; }, 1500);
        }
      }
    },
    async onToggleEnabled(enabled) {
      this.isToggling = true;
      const previous = this.allEnabled;
      this.allEnabled = enabled;
      try {
        const { data } = await http.post("/branch/extension-toggle", { enabled });
        const d = data?.data || {};
        this.enabledBanksCount = d.enabledBanksCount ?? this.enabledBanksCount;
        this.totalBanksCount = d.totalBanksCount ?? this.totalBanksCount;
        this.allEnabled = !!d.allEnabled;
      } catch (err) {
        this.allEnabled = previous; // revert
        this.$swal.fire({ icon: "error", title: "Toggle failed", text: err?.response?.data?.message || err.message });
      } finally {
        this.isToggling = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #cbd5e1; border-radius: 24px; transition: 0.3s; }
.slider:before { content: ""; position: absolute; height: 18px; width: 18px; left: 3px; bottom: 3px; background: #fff; border-radius: 50%; transition: 0.3s; }
.switch input:checked + .slider { background: #16a34a; }
.switch input:checked + .slider:before { transform: translateX(20px); }
.switch input:disabled + .slider { opacity: 0.5; cursor: not-allowed; }
.badge-green { background: #d1fae5; color: #065f46; }
.badge-red { background: #fee2e2; color: #991b1b; }
.badge-amber { background: #fef3c7; color: #92400e; }
</style>
